import moment from 'moment-timezone'
import { BANK_HOLIDAYS, BRAZIL_LOCALE, BRAZIL_TIMEZONE } from './constants'
import { subDays, addDays, addDaysInBrazil } from './math'
import { isSameDayInBrazil, isWeekendInBrazil, isBankWorkdayInBrazil, isBankHolidayInBrazil } from './logic'

export const getLocaleMoment = (locale: string = 'en') => {
  moment.locale(locale)
  return moment
}

export const getValueOf = (date: string | Date) => {
  return moment(date).valueOf()
}

export const startOfDayInBrazil = (date?: Date | string): Date => {
  const dateInBrazil = moment.tz(date || new Date(), BRAZIL_TIMEZONE)
  return dateInBrazil.startOf('day').toDate()
}

export const endOfDayInBrazil = (date?: Date | string): Date => {
  const dateInBrazil = moment.tz(date || new Date(), BRAZIL_TIMEZONE)
  return dateInBrazil.endOf('day').toDate()
}

export const endOfMonthInBrazil = (date?: Date | string): Date => {
  const dateInBrazil = moment.tz(date || new Date(), BRAZIL_TIMEZONE)
  return dateInBrazil.endOf('month').toDate()
}

export const formatInBrazil = (date: Date | string, format?: string): string => {
  return moment.tz(date, BRAZIL_TIMEZONE).format(format)
}

// YYYY-MM-DD
export const toDayStringInBrazil = (date: Date | string, format: string = 'YYYY-MM-DD', locale?: string): string => {
  const localMoment = getLocaleMoment(locale)
  return localMoment.tz(date, BRAZIL_TIMEZONE).format(format)
}

export const weekOfTheMonth = (date: Date | string) => {
  const day = moment(date)
  const firstDayOfMonth = day.clone().startOf('month')
  const firstDayOfWeek = firstDayOfMonth.clone().startOf('week').subtract(1, 'day')

  const offset = firstDayOfMonth.diff(firstDayOfWeek, 'days')

  return Math.ceil((day.date() + offset) / 7)
}

export const numberOfTheWeek = (date: Date | string) => {
  const day = moment(date).date()
  return Math.ceil(day / 7)
}

export const toISOStringInBrazil = (date: Date | string): string => {
  return moment.tz(date, BRAZIL_TIMEZONE).format()
}

// 2014-06-17T10:58:05-03:00
export const toISOString = (date: Date | string): string => {
  return moment(date).format()
}

export const getHourInBrazil = (date: Date | string): number => {
  return moment.tz(date, BRAZIL_TIMEZONE).hour()
}

export const getWeekdayInBrazil = (date: Date | string): number => {
  return moment.tz(date, BRAZIL_TIMEZONE).weekday()
}

// YYYY-MM-DD
export const fromDayStringInBrazil = (dayString: string, format?: string): Date => {
  return moment.tz(dayString, format || 'YYYY-MM-DD', true, BRAZIL_TIMEZONE).toDate()
}

export const BANK_HOLYDAY_DATES = BANK_HOLIDAYS.map(bankHoliday => {
  return moment.tz(bankHoliday, BRAZIL_TIMEZONE).toDate()
})

export const getLastBankWorkdayInBrazil = (baseDate: Date = new Date()): Date => {
  let bankWorkday = baseDate
  while (isWeekendInBrazil(bankWorkday) || isBankHolidayInBrazil(bankWorkday)) {
    bankWorkday = subDays(bankWorkday, 1)
  }
  return bankWorkday
}

export const getNextBankWorkdayInBrazil = (date: Date | string): Date => {
  let dateInBrazil = moment.tz(date, BRAZIL_TIMEZONE).toDate()
  while (isWeekendInBrazil(dateInBrazil) || isBankHolidayInBrazil(dateInBrazil)) {
    dateInBrazil = addDays(dateInBrazil, 1)
  }
  return dateInBrazil
}

export const setDayInBrazil = (date: Date, day: number): Date => {
  const dateInBrazil = moment.tz(date, BRAZIL_TIMEZONE)
  return dateInBrazil.date(day).toDate()
}

export const setMonthInBrazil = (date: Date, month: number): Date => {
  const dateInBrazil = moment.tz(date, BRAZIL_TIMEZONE)
  return dateInBrazil.month(month).toDate()
}

export const startOfMonth = (month: number): Date => {
  return moment().month(month).startOf('month').toDate()
}

export const startOfMonthByDate = (date: Date): Date => {
  return moment(date).startOf('month').toDate()
}

export const startOfLastMonthByDate = (date: Date): Date => {
  return moment(date).subtract(3, 'month').startOf('month').toDate()
}

export const endOfLastMonthByDate = (date: Date): Date => {
  return moment(date).subtract(1, 'month').endOf('month').toDate()
}

export const startOfMonthByDateInBrazil = (date?: Date | string): Date => {
  const dateInBrazil = moment.tz(date || new Date(), BRAZIL_TIMEZONE)
  return dateInBrazil.startOf('month').toDate()
}

export const endOfMonth = (month: number): Date => {
  return moment().month(month).endOf('month').toDate()
}

export const endOfMonthByDate = (date: Date): Date => {
  return moment(date).endOf('month').toDate()
}

export const endOfMonthByDateInBrazil = (date?: Date | string): Date => {
  const dateInBrazil = moment.tz(date || new Date(), BRAZIL_TIMEZONE)
  return dateInBrazil.endOf('month').toDate()
}

export const toDate = (date: string): Date => {
  const momentDate = moment(date)
  return momentDate.toDate()
}

export const unixToDate = (date: number): Date => {
  const momentDate = moment(date)
  return momentDate.toDate()
}

export const startOfWeekInBrazil = (date?: Date | string): Date => {
  const dateInBrazil = moment.tz(date || new Date(), BRAZIL_TIMEZONE)
  return dateInBrazil.startOf('isoWeek').toDate()
}

export const startOfMonthInBrazil = (date?: Date): Date => {
  const dateInBrazil = moment.tz(date || new Date(), BRAZIL_TIMEZONE)
  return dateInBrazil.startOf('month').toDate()
}

export const getDayOfMonthInBrazil = (date: Date): number => {
  const dateInBrazil = moment.tz(date, BRAZIL_TIMEZONE)
  return dateInBrazil.date()
}

export const toBrazilianDate = (date: Date | string): Date => {
  return moment.tz(date, BRAZIL_TIMEZONE).toDate()
}

export const getDiffInWorkDays = (lateDate: Date, earlierDate: Date): number => {
  let indexDate = earlierDate
  let totalWorkDays = isBankWorkdayInBrazil(lateDate) ? 1 : 0

  while (!isSameDayInBrazil(indexDate, lateDate)) {
    if (isBankWorkdayInBrazil(indexDate)) {
      totalWorkDays++
    }
    indexDate = addDaysInBrazil(indexDate, 1)
  }

  return totalWorkDays
}

export const nowInBrazil = (): Date => {
  return moment.tz(new Date(), BRAZIL_TIMEZONE).toDate()
}

export const getMonthNumberInBrazil = (date: Date): string => {
  const month = moment.tz(date, BRAZIL_TIMEZONE).month()
  return `${month + 1}`
}

export const getMonthNameInBrazil = (date: Date): string => {
  const month = moment.tz(date, BRAZIL_TIMEZONE).month()

  switch (month) {
    case 0:
      return 'Janeiro'
    case 1:
      return 'Fevereiro'
    case 2:
      return 'Março'
    case 3:
      return 'Abril'
    case 4:
      return 'Maio'
    case 5:
      return 'Junho'
    case 6:
      return 'Julho'
    case 7:
      return 'Agosto'
    case 8:
      return 'Setembro'
    case 9:
      return 'Outubro'
    case 10:
      return 'Novembro'
    case 11:
    default:
      return 'Dezembro'
  }
}

// Date: 2014-06-17T10:58:05-03:00
export const isoToDate = (date: string): Date => {
  return moment(date).toDate()
}

// YYYY-MM-DD
export const fromCustomPatternToDayStringInBrazil = (dayString: string, customPattern: string): string => {
  return moment.tz(dayString, customPattern, true, BRAZIL_TIMEZONE).format('YYYY-MM-DD')
}

export const formatDate = (date: Date | string, format: string): string => {
  return moment(date).format(format)
}

export const startOfTheDay = (date: string | Date): Date => {
  return moment(date).startOf('day').toDate()
}

export const endOfTheDay = (date: string): Date => {
  return moment(date).endOf('day').toDate()
}

// start from 0: january to 11: december
export const getCurrentMonth = (): number => {
  return moment.tz(new Date(), BRAZIL_TIMEZONE).month()
}

// start from 0: january to 11: december
export const getCurrentMonthAndYear = (): string => {
  return moment.tz(new Date(), BRAZIL_TIMEZONE).format('YYYY-MM')
}

export const newDateInBrazilFormat = (date: string | Date): Date => {
  return moment(date, 'DD/MM/YYYY').toDate()
}

export const getCurrentDay = (): number => {
  return +moment.tz(new Date(), BRAZIL_TIMEZONE).format('DD')
}

export const getISODateFromBrazilianStandard = (date: string) => {
  const [day, month, year] = date.split('-')

  if (!day || !month || !year) {
    return false
  }

  return `${year}-${month}-${day}`
}

export const formatToStringDate = (date: string | Date, formatPattern = 'D [de] MMMM [de] YYYY') => {
  return moment(date).locale(BRAZIL_LOCALE).format(formatPattern)
}

export const startOfYear = (year: number) => {
  return moment().year(year).startOf('year').toDate()
}

export const endOfYear = (year: number) => {
  return moment().year(year).endOf('year').toDate()
}
